'use client';

import { useState, useMemo } from 'react';
import {
  Search,
  Plus,
  Eye,
  Edit2,
  FolderKanban,
  Calendar,
  Users,
  TrendingUp,
} from 'lucide-react';
import { PageHeader } from '../../../components/shared/PageHeader';
import { Modal, FormField, FormInput, FormSelect, FormTextarea, Button } from '../../../components/shared/Modal';
import { projects } from '../data/mock-data';
import { projectStatusConfig, projectPriorityConfig, type Project } from '../types';
import { formatCurrency } from '../../../types';

// 프로젝트 등록 모달
function ProjectAddModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="프로젝트 등록"
      description="새로운 프로젝트를 등록합니다."
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>취소</Button>
          <Button onClick={onClose}>등록</Button>
        </>
      }
    >
      <div className="space-y-6">
        <div className="grid grid-cols-2 gap-4">
          <FormField label="프로젝트 코드" required>
            <FormInput placeholder="자동 생성" disabled />
          </FormField>
          <FormField label="프로젝트명" required>
            <FormInput placeholder="프로젝트명을 입력하세요" />
          </FormField>
          <FormField label="고객">
            <FormSelect>
              <option value="">선택하세요</option>
              <option value="c001">삼성전자</option>
              <option value="c002">LG전자</option>
              <option value="c003">CJ제일제당</option>
              <option value="c004">아모레퍼시픽</option>
            </FormSelect>
          </FormField>
          <FormField label="프로젝트 관리자" required>
            <FormSelect>
              <option value="">선택하세요</option>
              <option value="u001">김영업</option>
              <option value="u002">이마케팅</option>
              <option value="u003">박관리</option>
            </FormSelect>
          </FormField>
          <FormField label="시작일" required>
            <FormInput type="date" />
          </FormField>
          <FormField label="종료일" required>
            <FormInput type="date" />
          </FormField>
          <FormField label="우선순위" required>
            <FormSelect>
              <option value="">선택하세요</option>
              {Object.entries(projectPriorityConfig).map(([key, config]) => (
                <option key={key} value={key}>{config.label}</option>
              ))}
            </FormSelect>
          </FormField>
          <FormField label="예산">
            <FormInput type="number" min={0} placeholder="0" />
          </FormField>
        </div>
        <FormField label="프로젝트 설명">
          <FormTextarea rows={3} placeholder="프로젝트에 대한 설명을 입력하세요" />
        </FormField>
      </div>
    </Modal>
  );
}

export function ProjectListPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [selectedPriority, setSelectedPriority] = useState<string>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);

  const filteredData = useMemo(() => {
    return projects.filter((project) => {
      const matchesSearch =
        project.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
        project.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (project.customerName || '').toLowerCase().includes(searchQuery.toLowerCase());

      const matchesStatus = selectedStatus === 'all' || project.status === selectedStatus;
      const matchesPriority = selectedPriority === 'all' || project.priority === selectedPriority;

      return matchesSearch && matchesStatus && matchesPriority;
    });
  }, [searchQuery, selectedStatus, selectedPriority]);

  // 상태별 통계
  const statusStats = useMemo(() => {
    const stats: Record<string, number> = {};
    projects.forEach((project) => {
      stats[project.status] = (stats[project.status] || 0) + 1;
    });
    return stats;
  }, []);

  const getStatusColor = (color: string) => {
    const colors: Record<string, string> = {
      gray: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
      blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      yellow: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      orange: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
      red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    };
    return colors[color] || colors.gray;
  };

  const getPriorityColor = (color: string) => {
    const colors: Record<string, string> = {
      gray: 'text-gray-500',
      blue: 'text-blue-500',
      orange: 'text-orange-500',
      red: 'text-red-500',
    };
    return colors[color] || colors.gray;
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="프로젝트 관리"
        description="프로젝트를 조회하고 관리합니다."
        actions={
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="w-4 h-4" />
            프로젝트 등록
          </button>
        }
      />

      {/* 상태별 요약 */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {Object.entries(projectStatusConfig).map(([status, config]) => (
          <button
            key={status}
            onClick={() => setSelectedStatus(selectedStatus === status ? 'all' : status)}
            className={`p-3 rounded-lg border text-left transition-colors ${
              selectedStatus === status
                ? 'border-primary bg-primary/5'
                : 'border-border bg-card hover:bg-accent/50'
            }`}
          >
            <div className="text-xs text-muted-foreground">{config.label}</div>
            <div className="text-xl font-bold mt-1">{statusStats[status] || 0}개</div>
          </button>
        ))}
      </div>

      {/* 필터 */}
      <div className="flex flex-wrap gap-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="프로젝트 코드, 이름, 고객명 검색..."
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>

        <select
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
          className="h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">전체 상태</option>
          {Object.entries(projectStatusConfig).map(([status, config]) => (
            <option key={status} value={status}>
              {config.label}
            </option>
          ))}
        </select>

        <select
          value={selectedPriority}
          onChange={(e) => setSelectedPriority(e.target.value)}
          className="h-10 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="all">전체 우선순위</option>
          {Object.entries(projectPriorityConfig).map(([priority, config]) => (
            <option key={priority} value={priority}>
              {config.label}
            </option>
          ))}
        </select>
      </div>

      {/* 프로젝트 목록 */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredData.map((project) => {
          const statusConfig = projectStatusConfig[project.status];
          const priorityConfig = projectPriorityConfig[project.priority];
          const budgetUsage = project.budget > 0 ? (project.actualCost / project.budget) * 100 : 0;

          return (
            <div
              key={project.id}
              className="rounded-lg border border-border bg-card overflow-hidden"
            >
              <div className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                      <FolderKanban className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm text-primary">{project.code}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(statusConfig.color)}`}>
                          {statusConfig.label}
                        </span>
                      </div>
                      <div className="font-medium mt-1">{project.name}</div>
                      {project.customerName && (
                        <div className="text-sm text-muted-foreground">{project.customerName}</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <button className="p-1.5 rounded hover:bg-accent" title="보기">
                      <Eye className="w-4 h-4 text-muted-foreground" />
                    </button>
                    <button className="p-1.5 rounded hover:bg-accent" title="수정">
                      <Edit2 className="w-4 h-4 text-muted-foreground" />
                    </button>
                  </div>
                </div>

                {/* 진행률 */}
                <div className="mb-3">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-muted-foreground">진행률</span>
                    <span className="font-medium">{project.progress}%</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary rounded-full"
                      style={{ width: `${project.progress}%` }}
                    />
                  </div>
                </div>

                {/* 상세 정보 */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">
                      {project.startDate} ~ {project.endDate}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">{project.projectManagerName}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <TrendingUp className={`w-4 h-4 ${getPriorityColor(priorityConfig.color)}`} />
                    <span className="text-muted-foreground">우선순위: {priorityConfig.label}</span>
                  </div>
                  <div className="text-right">
                    <span className={`text-xs ${budgetUsage > 90 ? 'text-red-500' : budgetUsage > 70 ? 'text-yellow-500' : 'text-muted-foreground'}`}>
                      {formatCurrency(project.actualCost)} / {formatCurrency(project.budget)}
                    </span>
                  </div>
                </div>
              </div>

              {/* 예산 사용률 바 */}
              <div className="h-1 bg-muted">
                <div
                  className={`h-full ${
                    budgetUsage > 90
                      ? 'bg-red-500'
                      : budgetUsage > 70
                      ? 'bg-yellow-500'
                      : 'bg-green-500'
                  }`}
                  style={{ width: `${Math.min(budgetUsage, 100)}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {filteredData.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          검색 결과가 없습니다.
        </div>
      )}

      {/* 프로젝트 등록 모달 */}
      <ProjectAddModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}
